import clsx from "clsx";
import { Npc } from "gamedata/GameData";
import type { CSSProperties, ReactNode, RefObject } from "react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { GameVer, Lang, useRealm } from "~/util/Lang";
import { EntityTooltip } from "../site/Tooltip";
import css from "./Map.module.css";

type Point = { x: number; y: number };
type NpcSpawn = {
  npc: Npc;
  pos: Point[];
};

type MapInfo = {
  id: string;
  name: string;
  img: string;
  height: number;
  width: number;
  zeroX: number;
  zeroY: number;
  scale: number;
};

const ADEN_C1: MapInfo = {
  id: "aden",
  name: "Aden",
  img: "map/aden-c1.jpg",
  height: 1476,
  width: 1642,
  zeroX: 655,
  zeroY: 160,
  scale: 0.005,
};
const ELMORE_C1: MapInfo = {
  id: "elmore",
  name: "Elmore",
  img: "map/elmore-c1.jpg",
  height: 984,
  width: 1312,
  zeroX: 330,
  zeroY: 1310,
  scale: 0.005,
};

const ADEN_C2: MapInfo = {
  id: "aden",
  name: "Aden",
  img: "map-c2/aden-c2.jpg",
  height: 1476,
  width: 1642,
  zeroX: 655,
  zeroY: 160,
  scale: 0.005,
};
const ELMORE_C2: MapInfo = ELMORE_C1;

const ADEN_C3: MapInfo = {
  id: "aden",
  name: "Aden",
  img: "map-c3/aden-c3.jpg",
  height: 1476,
  width: 1642,
  zeroX: 655,
  zeroY: 160,
  scale: 0.005,
};
const ELMORE_C3: MapInfo = {
  id: "elmore",
  name: "Elmore",
  img: "map-c3/elmore-c3.jpg",
  height: 984,
  width: 1312,
  zeroX: 330,
  zeroY: 1310,
  scale: 0.005,
};

const ELMORE_ADEN_C4: MapInfo = {
  id: "aden",
  name: "Aden",
  img: "maps-c4/elmore-aden.jpg",
  height: 2620,
  width: 1812,
  zeroX: 655,
  zeroY: 1303,
  scale: 0.005,
};

const ELMORE_ADEN_C5: MapInfo = {
  id: "aden",
  name: "Aden",
  img: "maps-c5/elmore-aden.jpg",
  height: 2620,
  width: 1812,
  zeroX: 655,
  zeroY: 1303,
  scale: 0.005,
};

const ELMORE_ADEN_IL: MapInfo = {
  id: "aden",
  name: "Aden",
  img: "maps-il/elmore-aden.jpg",
  height: 2620,
  width: 1812,
  zeroX: 655,
  zeroY: 1303,
  scale: 0.005,
};

const ADEN_GF: MapInfo = {
  id: "aden",
  name: "Aden",
  img: "maps-gf/elmore-aden.jpg",
  height: 2620,
  width: 1977,
  zeroX: 809,
  zeroY: 1303,
  scale: 0.005,
};

const MAPS: Record<`${GameVer}-${Lang}`, MapInfo[]> = {
  "c1-en": [ADEN_C1, ELMORE_C1],
  "c1-ru": [ADEN_C1, ELMORE_C1],
  "c2-en": [ADEN_C2, ELMORE_C2],
  "c2-ru": [ADEN_C2, ELMORE_C2],
  "c3-en": [ADEN_C3, ELMORE_C3],
  "c3-ru": [ADEN_C3, ELMORE_C3],
  "c4-en": [ELMORE_ADEN_C4],
  "c4-ru": [ELMORE_ADEN_C4],
  "c5-en": [ELMORE_ADEN_C5],
  "c5-ru": [ELMORE_ADEN_C5],
  "il-en": [ELMORE_ADEN_IL],
  "il-ru": [ELMORE_ADEN_IL],
  "gf-en": [ADEN_GF],
  "gf-ru": [ADEN_GF],
};

type MapState = {
  map: MapInfo;
  offset: Point;
};

export function Map(props: {
  spawns?: Pick<NpcSpawn, "pos">[];
  bosses?: ReadonlyArray<{
    npc: { npcName: string; level: number | null };
    pos: ReadonlyArray<{ x: number; y: number; z: number }>;
  }>;
  teleports?: ReadonlyArray<{ from: Point; to: Point }>;
  fullscreen?: Boolean;
  onClick?: (point: Point) => void;
}) {
  const realm = useRealm();
  const realmId = `${realm.game}-${realm.lang}` as const;
  const maps = MAPS[realmId];

  return <MapInner key={realmId} maps={maps} {...props} />;
}

function MapInner(props: {
  maps: MapInfo[];
  spawns?: Pick<NpcSpawn, "pos">[];
  bosses?: ReadonlyArray<{
    npc: { npcName: string; level: number | null };
    pos: ReadonlyArray<{ x: number; y: number; z: number }>;
  }>;
  teleports?: ReadonlyArray<{ from: Point; to: Point }>;
  fullscreen?: Boolean;
  onClick?: (point: Point) => void;
}) {
  const fullscreen = props.fullscreen ?? false;
  const spawns = props.spawns ?? [];
  const [mapStates, setMapStates] = useState<Record<string, MapState>>(() => {
    const state: Record<
      string,
      { map: MapInfo; offset: { x: number; y: number } }
    > = {};
    for (const map of props.maps) {
      state[map.id] = { map, offset: { x: 0, y: 0 } };
    }
    return state;
  });
  const [mapId, setMapId] = useState<string>("aden");
  const map = mapStates[mapId];
  function setOffset(offset: Point) {
    setMapStates({ ...mapStates, [mapId]: { ...map, offset } });
  }

  const [boss, setBoss] = useState<number | null>(null);

  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (boxRef.current != null) {
      const pos = spawns[0]?.pos;
      if (pos) {
        for (const mapInfo of props.maps) {
          const map = mapStates[mapInfo.id];
          const point = pointToPx(map.map, pos[0]);
          if (
            point.x >= 0 &&
            point.x <= map.map.width &&
            point.y >= 0 &&
            point.y <= map.map.height
          ) {
            const offset = clampOffset(
              {
                x: -(point.x - boxRef.current.offsetWidth / 2),
                y: -(point.y - boxRef.current.offsetHeight / 2),
              },
              map.map,
              {
                offset: { x: 0, y: 0 },
                height: boxRef.current.offsetHeight,
                width: boxRef.current.offsetWidth,
              },
            );
            setMapId(map.map.id);
            setMapStates({ ...mapStates, [map.map.id]: { ...map, offset } });
            break;
          }
        }
      }
    }
  }, []);

  const onBossClick = useCallback((id: number) => setBoss(id), [setBoss]);

  let content = null;
  if (map) {
    content = (
      <Droppable>
        <Draggable
          boxRef={boxRef}
          offset={map.offset}
          map={map.map}
          onMove={(offset) => {
            setOffset(offset);
          }}
          onClick={(point) => props.onClick?.(pxToPoint(map.map, point))}
          onBossClick={onBossClick}
        >
          <div
            style={{
              height: map.map.height,
              width: map.map.width,
              position: "relative",
            }}
          >
            <img
              src={`/s/${map.map.img}`}
              style={{
                display: "block",
                height: map.map.height,
                width: map.map.width,
              }}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              style={{
                top: 0,
                left: 0,
                height: map.map.height,
                width: map.map.width,
                position: "absolute",
              }}
            >
              {spawns.map((spawn, i) => {
                const points = spawn.pos.map((p) => pointToPx(map.map, p));
                if (points.length === 1) {
                  const p = points[0];
                  return (
                    <Fragment key={i}>
                      <path
                        d={
                          `M ${p.x - 6} ${p.y} L ${p.x + 6} ${p.y} ` +
                          `M ${p.x} ${p.y - 6} L ${p.x} ${p.y + 6}`
                        }
                        strokeWidth="3"
                        stroke="#fff"
                      />
                      <path
                        d={
                          `M ${p.x - 5} ${p.y} L ${p.x + 5} ${p.y} ` +
                          `M ${p.x} ${p.y - 5} L ${p.x} ${p.y + 5}`
                        }
                        strokeWidth="1"
                        stroke="red"
                      />
                    </Fragment>
                  );
                }
                return (
                  <path
                    key={i}
                    d={
                      points
                        .map((p, i) => `${i ? "L" : "M"} ${p.x} ${p.y}`)
                        .join(" ") + " Z"
                    }
                    fill="#f00a"
                  />
                );
              })}
              {props.bosses?.map((spawn, i) => (
                <Boss
                  key={i}
                  map={map.map}
                  spawn={spawn}
                  active={i === boss}
                  id={i}
                  onDismiss={() => setBoss(null)}
                />
              ))}
              {props.teleports?.map((teleport, i) => (
                <Teleport
                  key={i}
                  map={map.map}
                  teleport={teleport}
                  active={i === boss}
                  id={i}
                  onDismiss={() => setBoss(null)}
                />
              ))}
            </svg>
          </div>
        </Draggable>
      </Droppable>
    );
  }

  return (
    <>
      <div className={css.tabs}>
        {props.maps.map((m) => (
          <MapTab
            key={m.id}
            active={m.id === map.map?.id}
            onClick={() => setMapId(m.id)}
          >
            {m.name}
          </MapTab>
        ))}
      </div>
      <div
        ref={boxRef}
        className={css.box}
        style={fullscreen ? { width: "100%", height: 600 } : {}}
      >
        {content}
        <div className={css.shadow} />
      </div>
    </>
  );
}

function clampOffset(
  offset: Point,
  map: MapInfo,
  box: { offset: Point; width: number; height: number },
) {
  const ret = {
    x: Math.min(
      -box.offset.x,
      Math.max(offset.x, -map.width + box.width - box.offset.x),
    ),
    y: Math.min(
      -box.offset.y,
      Math.max(offset.y, -map.height + box.height - box.offset.y),
    ),
  };
  return ret;
}

function pointToPx(map: MapInfo, point: Point): Point {
  return {
    x: map.zeroX + point.x * map.scale,
    y: map.zeroY + point.y * map.scale,
  };
}

function pxToPoint(map: MapInfo, point: Point): Point {
  return {
    x: (point.x - map.zeroX) / map.scale,
    y: (point.y - map.zeroY) / map.scale,
  };
}

function Droppable(props: { children: ReactNode }) {
  const style: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
  };

  return <div style={style}>{props.children}</div>;
}

type DragState = {
  readonly kind:
    | Readonly<{ t: "mouse" }>
    | Readonly<{ t: "touch"; id: number }>;
  readonly zero: Point;

  diff: Point;
  moved: boolean;
};

function identifiedTouch(touchList: TouchList, id: number) {
  for (let i = 0; i < touchList.length; ++i) {
    const touch = touchList.item(i);
    if (touch?.identifier === id) {
      return touch;
    }
  }
  return null;
}

export function Draggable(props: {
  boxRef: RefObject<HTMLDivElement>;
  children: ReactNode;
  map: MapInfo;
  offset: Point;
  onMove: (offset: Point) => void;
  onClick: (point: Point) => void;
  onBossClick: (id: number) => void;
}) {
  const [drag, setDrag] = useState<null | { state: DragState; diff: Point }>(
    () => null,
  );
  const style: CSSProperties = {
    transform: drag
      ? `translate3d(${drag.diff.x}px, ${drag.diff.y}px, 0)`
      : undefined,
    top: props.offset.y,
    left: props.offset.x,
    position: "absolute",
  };

  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (drag === null) {
      return;
    }

    function onMove(clientPos: Point) {
      const box = props.boxRef.current;
      if (drag && box) {
        const diff = clampOffset(
          {
            x: clientPos.x - drag.state.zero.x,
            y: clientPos.y - drag.state.zero.y,
          },
          props.map,
          {
            offset: props.offset,
            width: box.offsetWidth,
            height: box.offsetHeight,
          },
        );

        drag.state.moved = true;
        drag.state.diff = diff;
        setDrag({
          state: drag.state,
          diff,
        });
      }
    }

    function onUp(e: MouseEvent | TouchEvent) {
      const box = props.boxRef.current;
      if (drag && box) {
        if (drag.state.moved) {
          props.onMove({
            x: props.offset.x + drag.state.diff.x,
            y: props.offset.y + drag.state.diff.y,
          });
        } else {
          const target = e.target as HTMLElement | null;
          const bossAttr = target?.dataset.boss;
          if (bossAttr) {
            props.onBossClick(parseInt(bossAttr));
          } else if (mapRef.current) {
            const bcr = mapRef.current.getBoundingClientRect();
            const x = drag.state.zero.x - bcr.left;
            const y = drag.state.zero.y - bcr.top;
            props.onClick({ x, y });
          }
        }

        setDrag(null);
      }
    }

    if (drag.state.kind.t === "touch") {
      const kind = drag.state.kind;
      function onMoveTouch(e: TouchEvent) {
        const touch = identifiedTouch(e.touches, kind.id);
        if (touch) {
          onMove({ x: touch.clientX, y: touch.clientY });
        }
      }
      // https://github.com/bevacqua/dragula/issues/487#issuecomment-383857371
      document.addEventListener("touchmove", onMoveTouch, { passive: false });
      document.addEventListener("touchend", onUp);

      return () => {
        document.removeEventListener("touchmove", onMoveTouch);
        document.removeEventListener("touchend", onUp);
      };
    } else if (drag.state.kind.t === "mouse") {
      function onMoveMouse(e: MouseEvent) {
        e.preventDefault();
        onMove({ x: e.clientX, y: e.clientY });
      }
      window.addEventListener("mousemove", onMoveMouse);
      window.addEventListener("mouseup", onUp);

      return () => {
        window.removeEventListener("mousemove", onMoveMouse);
        window.removeEventListener("mouseup", onUp);
      };
    }
  }, [drag, setDrag, props.onBossClick]);

  useEffect(() => {
    function handle(e: TouchEvent) {
      e.preventDefault();
      const touch = e.changedTouches.item(0)!;
      const zero = {
        x: touch.clientX,
        y: touch.clientY,
      };
      const diff = { x: 0, y: 0 };
      setDrag({
        state: {
          kind: { t: "touch", id: touch.identifier },
          zero,
          moved: false,
          diff,
        },
        diff,
      });
    }
    const el = mapRef.current!;
    el.addEventListener("touchstart", handle, { passive: false });
    return () => {
      el.removeEventListener("touchstart", handle);
    };
  }, [setDrag]);

  return (
    <div
      ref={mapRef}
      style={style}
      onMouseDown={(e) => {
        const zero = { x: e.clientX, y: e.clientY };
        const diff = { x: 0, y: 0 };
        setDrag({
          state: {
            kind: { t: "mouse" },
            zero,
            moved: false,
            diff,
          },
          diff,
        });
        e.preventDefault();
      }}
    >
      {props.children}
    </div>
  );
}

function MapTab(props: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <span
      tabIndex={0}
      className={clsx([css.tab], { [css.tabActive]: props.active })}
      onClick={() => props.onClick()}
    >
      {props.children}
    </span>
  );
}

function Boss(props: {
  map: MapInfo;
  spawn: {
    npc: { npcName: string; level: number | null };
    pos: ReadonlyArray<{ x: number; y: number; z: number }>;
  };
  active: boolean;
  id: number;
  onDismiss: () => void;
}) {
  const spawn = props.spawn;
  const points = spawn.pos.map((p) => pointToPx(props.map, p));
  const point: Point = {
    x: points.reduce((acc, p) => acc + p.x, 0) / points.length,
    y: points.reduce((acc, p) => acc + p.y, 0) / points.length,
  };

  const [hover, setHover] = useState(false);

  return (
    <EntityTooltip
      what={{ t: "npc", uname: spawn.npc.npcName }}
      force={props.active}
      onOpenChange={(open) => {
        if (!open) {
          props.onDismiss();
        }
      }}
    >
      {({ setReference, getReferenceProps }) => (
        <>
          <circle
            ref={setReference}
            {...getReferenceProps({
              onMouseEnter: () => setHover(true),
              onMouseLeave: () => setHover(false),
            })}
            cx={point.x}
            cy={point.y}
            r="12"
            fill="#0074d9"
            stroke={props.active ? "#fff" : hover ? "#ccc" : "#aaa"}
            data-boss={props.id}
          ></circle>
          <text
            x={point.x}
            y={point.y}
            textAnchor="middle"
            dy="0.3em"
            fontSize="12"
            fill="white"
            pointerEvents="none"
          >
            {spawn.npc.level}
          </text>
        </>
      )}
    </EntityTooltip>
  );
}

function Teleport(props: {
  map: MapInfo;
  teleport: { from: Point; to: Point };
  active: boolean;
  id: number;
  onDismiss: () => void;
}) {
  const [hover, setHover] = useState(false);

  const from = pointToPx(props.map, props.teleport.from);
  const to = pointToPx(props.map, props.teleport.to);
  const len = Math.sqrt((from.x - to.x) ** 2 + (from.y - to.y) ** 2);
  const angleRad = Math.atan2(to.y - from.y, to.x - from.x);

  return (
    <EntityTooltip
      what={{ t: "npc", uname: "clavier" }}
      force={props.active}
      onOpenChange={(open) => {
        if (!open) {
          props.onDismiss();
        }
      }}
    >
      {({ setReference, getReferenceProps }) => (
        <>
          <line
            x1={from.x}
            y1={from.y}
            x2={to.x}
            y2={to.y}
            strokeWidth="1"
            stroke="#22b0ffb0"
          />
        </>
      )}
    </EntityTooltip>
  );
}
